import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
	GeneralInformationLineMessageValidationConst,
	MsgTypeLineOptionConst,
} from '../../../../../core/const';
import { Parameter } from '../../../../../core/models/util/api-adapter-service-models';
import { AppResponse } from '../../../../../core/models/util/api-dto/response-models';
import { ApiAdapterServiceService } from '../../../../../core/services';
import { TemplateWAModel, WhatsAppAccountDto } from '../../../../models';
@Component({
	selector: 'lib-general-information-line',
	templateUrl: './general-information-line.component.html',
	styleUrls: ['./general-information-line.component.scss'],
})
export class GeneralInformationLineComponent implements OnInit, OnDestroy {
	@Output()
	public updateGeneralInformationForm: EventEmitter<FormGroup>;

	public form: FormGroup;
	public msgTypeWAOption: any[] = [];
	public validationMessages: any;
	public accountOption: WhatsAppAccountDto[] = [];
	public isHaveNextAccountOption: boolean = true;
	public isLoadingAccountOption: boolean = false;
	public templateOption: TemplateWAModel[] = [];
	public isHaveNextTemplateOption: boolean = true;
	public isLoadingTemplateOption: boolean = false;

	private pageNoAccountOption: number = 1;
	private pageNoTemplateOption: number = 1;
	private subscribers: Subscription[] = [];

	@Input() public isReadOnlyMode: boolean;

	@Input()
	set isTouchedForm(status: boolean) {
		if (!this.updateGeneralInformationForm) {
			return;
		}

		setTimeout(() => {
			this.updateGeneralInformationForm.emit(this.form);
		});
	}

	constructor(private apiAdapterService: ApiAdapterServiceService) {
		this.updateGeneralInformationForm = new EventEmitter();
	}

	ngOnInit(): void {
		this.msgTypeWAOption = MsgTypeLineOptionConst;
		this.validationMessages = GeneralInformationLineMessageValidationConst;

		this.initForm();
		this.initDisable();
		this.initEdit();
		this.initValidation();
		this.getAccountOptionService(
			this.getCampaignStorage?.generalInformation?.form?.medium === 'LINE'
				? this.getCampaignStorage?.generalInformation?.form?.from?.name
				: ''
		);
	}

	get getCampaignStorage(): any {
		return localStorage.getItem('telin.campaign')
			? JSON.parse(localStorage.getItem('telin.campaign'))
			: {};
	}

	private initForm(): void {
		this.form = new FormGroup({
			channel: new FormControl(null, Validators.required),
			from: new FormControl(null, Validators.required),
			text: new FormControl(null),
			template: new FormControl(null),
			isJSON: new FormControl(false),
			paramBody: new FormArray([], Validators.required),
		});

		this.form.valueChanges.subscribe(() => {
			setTimeout(() => {
				this.updateGeneralInformationForm.emit(this.form);
			});
		});
	}

	private initDisable(): void {
		if (!this.isReadOnlyMode) {
			return;
		}

		this.form.disable();
	}

	private initEdit(): void {
		if (
			Object.entries(this.getCampaignStorage).length === 0 ||
			this.getCampaignStorage?.generalInformation?.form?.medium !== 'LINE'
		)
			return;

		this.form
			.get('channel')
			.setValue(
				this.getCampaignStorage?.generalInformation?.form.channel
			);
		this.form
			.get('from')
			.setValue(this.getCampaignStorage?.generalInformation?.form.from);
		this.form
			.get('text')
			.setValue(this.getCampaignStorage?.generalInformation?.form.text);
		this.form
			.get('template')
			.setValue(
				this.getCampaignStorage?.generalInformation?.form.template
			);

		this.getTemplateOptionService(
			null,
			this.form.get('template').value?.name
		);
	}

	private initValidation(): void {
		for (const key in this.form.controls) {
			this.form.controls[key].clearValidators();
			this.form.controls[key].updateValueAndValidity();
		}

		this.form.get('channel').setValidators(Validators.required);
		this.form.get('from').setValidators(Validators.required);

		if (this.isText) {
			this.form.get('text').setValidators(Validators.required);
			this.form.get('text').updateValueAndValidity();
			return;
		}

		this.form.get('template').setValidators(Validators.required);
		this.form.get('template').updateValueAndValidity();
		this.form.get('paramBody').setValidators(Validators.required);
		this.form.get('paramBody').updateValueAndValidity();
	}

	public formReset(): void {
		this.form.get('text').reset();
		this.form.get('template').reset();
		(this.form.get('paramBody') as FormArray).controls = [];
		(this.form.get('paramBody') as FormArray).reset();

		this.initValidation();
	}

	private initParam(_name: string, _value: any, _type: string): Parameter {
		return {
			name: _name,
			value: _value,
			in: 'query',
			type: _type,
		};
	}

	private getAccountOptionService(
		filter: string = '',
		isInfinite: boolean = false
	): void {
		this.isLoadingAccountOption = true;

		const parameterList: Array<Parameter> = [
			this.initParam('pageNo', this.pageNoAccountOption, 'number'),
			this.initParam('rowPerPage', 20, 'number'),
			this.initParam('platform', 'LINE', 'string'),
			this.initParam('status', 'APPROVED', 'string'),
			this.initParam('filter', filter, 'string'),
		];

		const subs = this.apiAdapterService
			.executeApiRequest(parameterList, '/admin/account', 'GET')
			.subscribe((resp: AppResponse<WhatsAppAccountDto[]>) => {
				if (!resp.data) return;

				this.accountOption = isInfinite
					? [...this.accountOption, ...resp.data]
					: resp.data;
				this.isHaveNextAccountOption = resp.haveNext;
				this.isLoadingAccountOption = false;
			});

		this.subscribers.push(subs);
	}

	public getTemplateOptionService(
		event: string = null,
		filter: string = '',
		isInfinite: boolean = false
	): void {
		this.isLoadingTemplateOption = true;

		const parameterList: Array<Parameter> = [
			this.initParam('pageNo', this.pageNoTemplateOption, 'number'),
			this.initParam('rowPerPage', 20, 'number'),
			this.initParam('platform', 'LINE', 'string'),
			this.initParam('filter', filter, 'string'),
			this.initParam(
				'clientId',
				this.form.get('from').value?.clientId,
				'string'
			),
		];

		if (this.isReadOnlyMode)
			parameterList.push(this.initParam('summary', true, 'string'));

		const subs = this.apiAdapterService
			.executeApiRequest(parameterList, '/message/template', 'GET')
			.subscribe((resp: AppResponse<TemplateWAModel[]>) => {
				this.templateOption = isInfinite
					? [...this.templateOption, ...resp.data]
					: resp.data;
				this.isHaveNextTemplateOption = resp.haveNext;
				this.isLoadingTemplateOption = false;

				const template = this.templateOption?.find(
					t => t.ID === this.form.get('template').value?.ID
				);

				if (template) {
					this.form.get('template').setValue(template);
					this.updateTemplate(this.form.get('template').value);
				}
			});

		this.subscribers.push(subs);
	}

	private initParameter(value: string = ''): FormGroup {
		const form = new FormGroup({
			name: new FormControl(
				{ value: value, disabled: this.isReadOnlyMode },
				Validators.required
			),
		});

		return form;
	}

	get getParamBody(): FormGroup[] {
		return (this.form.get('paramBody') as FormArray)
			.controls as FormGroup[];
	}

	public addParamBody(value: string = ''): void {
		const form = this.initParameter(value);
		(this.form.get('paramBody') as FormArray).push(form);
	}

	get isText(): boolean {
		return (
			this.form.get('channel').value &&
			this.form.get('channel').value?.toLowerCase() === 'text'
		);
	}

	get isTemplate(): boolean {
		return (
			this.form.get('channel').value &&
			this.form.get('channel').value?.toLowerCase() === 'template'
		);
	}

	public getNextAccountBatch() {
		if (!this.isHaveNextAccountOption) return;

		this.pageNoAccountOption += 1;
		this.getAccountOptionService('', true);
	}

	public accountSelectedDisplay(
		attribute1: WhatsAppAccountDto,
		attribute2: WhatsAppAccountDto
	): string {
		return attribute1 && attribute2 && attribute1.id == attribute2.id
			? `${attribute1.name} (${attribute1.accountNo})`
			: '';
	}

	public getNextTemplateBatch() {
		if (!this.isHaveNextTemplateOption) return;

		this.pageNoTemplateOption += 1;
		this.getTemplateOptionService(null, '', true);
	}

	public templateSelectedDisplay(
		attribute1: TemplateWAModel,
		attribute2: TemplateWAModel
	): string {
		return attribute1 && attribute2 && attribute1.name == attribute2.name
			? `${attribute1.name}`
			: '';
	}

	public updateTemplate(template: TemplateWAModel): void {
		if (!template) return;

		let bodyText = template.body.text;

		if (this.isJsonString(template.body.text)) {
			bodyText = JSON.parse(template.body.text).text;
		}

		this.form.get('isJSON').setValue(this.isJsonString(template.body.text));
		(this.form.get('paramBody') as FormArray).controls = [];
		(this.form.get('paramBody') as FormArray).reset();

		const findBrackets = bodyText?.match(/{{/g);
		const formStorage = this.getCampaignStorage?.generalInformation?.form;

		findBrackets?.length > 0
			? this.form.get('paramBody').setValidators(Validators.required)
			: this.form.get('paramBody').clearValidators();

		findBrackets?.forEach((_, index) => {
			this.addParamBody(
				formStorage?.paramBody?.[index]?.name ??
					formStorage?.paramBody?.[index] ??
					''
			);
		});

		this.form.get('paramBody').updateValueAndValidity();
	}

	private isJsonString(str: string): boolean {
		try {
			JSON.parse(str);
		} catch (e) {
			return false;
		}

		return true;
	}

	public updateSearch(searchValue: string): void {
		this.pageNoTemplateOption = 1;
		this.getTemplateOptionService(null, searchValue);
	}

	public updateSearchAccount(searchValue: string): void {
		this.pageNoAccountOption = 1;
		this.getAccountOptionService(searchValue);
	}

	ngOnDestroy(): void {
		this.subscribers.forEach(each => each.unsubscribe());
	}
}
